import axios from 'axios';
import { API_PATH } from 'config';
import { createContext, useContext, useEffect, useState } from 'react';
import { apiAssetUrl } from 'utils/apiAssetUrl';

// Provider que carga el branding público del tenant actual (resuelto por el
// backend desde window.location.hostname) ANTES del login. Lo consume el
// componente Logo, Footer, SignIn y cualquier vista que quiera mostrar el
// nombre/logo del tenant.
//
// Llama a GET /tenants/current (público, sin auth — Fase 5). Si la respuesta
// incluye logo_url, también actualiza el favicon del documento.

const TenantBrandingContext = createContext(null);

const DEFAULT_BRANDING = {
  id: null,
  organization: 'LiftLogic',
  logo_url: null,
  subdomain: null,
  customDomain: null,
  primary_color: '#0055b3',
  secondary_color: '#c5561d',
};

export const TenantBrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    axios
      .get(`${API_PATH}/tenants/current`, {
        headers: { 'Content-Type': 'application/json' },
      })
      .then((res) => {
        if (cancelled) return;
        setBranding(res.data);
        if (res.data?.organization) {
          document.title = res.data.organization;
        }
        if (res.data?.logo_url) {
          const favicon = document.querySelector('link[rel="icon"]');
          if (favicon) favicon.setAttribute('href', apiAssetUrl(res.data.logo_url));
        }
      })
      .catch((err) => {
        if (cancelled) return;
        // Si el dominio no resuelve (404 TENANT_NOT_FOUND) o el backend está
        // caído, se cae al branding default para que el UI no quede bloqueado.
        // eslint-disable-next-line no-console
        console.warn(
          '[TenantBranding] No se pudo resolver el tenant:',
          err?.response?.data ?? err?.message
        );
        setError(err);
        setBranding(DEFAULT_BRANDING);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <TenantBrandingContext.Provider value={{ branding, loading, error }}>
      {children}
    </TenantBrandingContext.Provider>
  );
};

export const useTenantBranding = () => {
  const ctx = useContext(TenantBrandingContext);
  // Si el componente se usa fuera del provider (p.ej. tests), devolver default
  // para no romper. El caller puede opt-in a loading/error si le importa.
  if (!ctx) return { branding: DEFAULT_BRANDING, loading: false, error: null };
  return ctx;
};
